import { useCallback, useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { MAJOR_ARCANA } from '@lunary/tarot-data'
import { CARD_RATIO } from '@lunary/app-core/deck-model'
import { FlipCard } from './flip-card'
import { FLIP_VARIANTS, randomIndex } from '@lunary/app-core/flip-variants'
import { shuffled, useDeal } from './deal-layouts'
import styles from './card-game.module.css'

type Props = {
  count?: number
  maxWidth?: number
  maxScale?: number
}

const GAP = 10

function drawHand(count: number): number[] {
  return shuffled(MAJOR_ARCANA.map((c) => c.number)).slice(0, count)
}

function useStageWidth() {
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)
  useEffect(() => {
    const el = ref.current
    if (!el) return undefined
    const ro = new ResizeObserver(([entry]) => setWidth(entry?.contentRect.width ?? 0))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return [ref, width] as const
}

export function CardGame({ count = 5, maxWidth = 104, maxScale = 1.9 }: Props) {
  const [stageRef, stageWidth] = useStageWidth()
  const w = Math.round(Math.max(52, Math.min(maxWidth, (stageWidth - GAP * (count - 1)) / (count + 0.4))))
  const scale = Math.min(maxScale, (stageWidth * 0.7) / w)
  const [hand, setHand] = useState(() => drawHand(count))
  const [back, setBack] = useState(() => randomIndex(FLIP_VARIANTS.length))
  const [chosen, setChosen] = useState<number | null>(null)
  const [centered, setCentered] = useState(false)
  const [revealed, setRevealed] = useState(false)
  const onRedeal = useCallback(() => {
    setHand(drawHand(count))
    setBack(randomIndex(FLIP_VARIANTS.length))
  }, [count])
  const deal = useDeal(count, w, GAP, onRedeal)

  useEffect(() => {
    if (chosen === null) return undefined
    const t = window.setTimeout(() => setCentered(true), 650)
    return () => window.clearTimeout(t)
  }, [chosen])

  const againTimer = useRef<number | undefined>(undefined)
  useEffect(() => () => window.clearTimeout(againTimer.current), [])

  const again = () => {
    setRevealed(false)
    setCentered(false)
    setChosen(null)
    againTimer.current = window.setTimeout(deal.redeal, 700)
  }

  const card = chosen !== null ? MAJOR_ARCANA.find((c) => c.number === hand[chosen]) : undefined
  const height = w * CARD_RATIO
  const stageStyle = { '--w': `${w}px`, height: height * scale + 28 } as CSSProperties
  const ready = deal.phase === 'ready' && chosen === null

  return (
    <div className={styles.game}>
      <div ref={stageRef} className={styles.stage} style={stageStyle}>
        {stageWidth > 0 &&
          hand.map((number, i) => {
            const isChosen = chosen === i
            const gone = chosen !== null && !isChosen
            const k = i - (count - 1) / 2
            const style: CSSProperties = isChosen
              ? { transform: `translate(0, 0) scale(${scale})`, transitionDuration: '700ms', transitionDelay: '0ms', zIndex: 40 }
              : gone
                ? { transform: `translate(${k * w * 1.6}px, ${height * 1.4}px) rotate(${k * 24}deg) scale(0.7)`, opacity: 0, transitionDelay: `${Math.abs(k) * 40}ms`, transitionDuration: '620ms', zIndex: 5 }
                : deal.styleFor(i)
            const name = MAJOR_ARCANA.find((c) => c.number === number)?.name ?? ''
            return (
              <div key={i} className={styles.slot} style={style}>
                <div className={deal.shuffling ? styles.wiggle : undefined} style={{ '--sx': (i % 2 ? 1 : -1) * (0.25 + i * 0.05) } as CSSProperties}>
                  <FlipCard
                    key={deal.round}
                    number={number}
                    name={name}
                    open={isChosen && centered}
                    width={w}
                    variant={back}
                    label={`Карта ${i + 1}`}
                    onOpen={ready ? () => setChosen(i) : undefined}
                    onRevealed={() => setRevealed(true)}
                  />
                </div>
              </div>
            )
          })}
      </div>
      <p className={`${styles.hint} ${ready ? '' : styles.hidden}`}>Нажмите на любую карту</p>
      {card && revealed ? (
        <div className={styles.result} aria-live="polite">
          <h3 className={styles.name}>{card.name}</h3>
          <div className={styles.keywords}>
            {card.uprightKeywords.slice(0, 3).map((k) => (
              <span key={k}>{k}</span>
            ))}
          </div>
          <p className={styles.meaning}>{card.uprightMeaning}</p>
          <button type="button" className={styles.again} onClick={again}>
            Выбрать другую карту
          </button>
        </div>
      ) : null}
    </div>
  )
}
